"use client";

import { useState, useEffect, useRef } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { X } from "lucide-react";

import { useStyles } from "./styles";
import { ViewMode } from "@/types/thread";
import { useAppDispatch, useAppSelector } from "@/redux/store";
import {
  createRoom,
  getNewGenerations,
  setShowSignupModal,
  getQuotas,
} from "@/redux/actions";
import { useGenContext } from "@/context/GenContext";
import useDeepCompareEffect from "@/hooks/useDeepCompareEffect";
import Header from "./Header";
import ModeSelect from "./ModeSelect";
import AspectRatioSelect from "./AspectRatioSelect";
import ModelSelect from "./ModelSelect";
import TotalImagesSelect from "./TotalImagesSelect";
import PreviewOverview from "./PreviewOverview";
import CollapsedView from "./CollapsedView";
import DailyGensSummary from "./DailyGensSummary";
import DailyGensSummaryCompact from "./DailyGensSummaryCompact";

const findScrollParent = (
  element: HTMLElement | null
): HTMLElement | Window => {
  if (!element) return window;

  let parent = element.parentElement;
  while (parent) {
    const style = window.getComputedStyle(parent);
    if (
      style.overflowY === "auto" ||
      style.overflowY === "scroll" ||
      style.overflow === "auto" ||
      style.overflow === "scroll"
    ) {
      return parent;
    }
    parent = parent.parentElement;
  }
  return window;
};

interface MobileLayoutProps {
  showHeading?: boolean;
  existingRoomUUID?: string;
  onClose?: () => void;
  collapsed?: boolean;
  hideSafeSearch?: boolean;
  initialConfig?: {
    prompt?: string;
    images?: File[];
    selectedModes?: {
      isPublic: boolean;
      is18Plus: boolean;
      isTextImage: boolean;
    };
    aspectRatio?: string;
    selectedModel?: string;
    totalImages?: number;
  };
}

const MobileLayout: React.FC<MobileLayoutProps> = ({
  showHeading = true,
  existingRoomUUID,
  onClose,
  collapsed = false,
  hideSafeSearch = false,
  initialConfig,
}) => {
  const { classes, cx } = useStyles();
  const dispatch = useAppDispatch();
  const router = useRouter();
  const searchParams = useSearchParams();
  const {
    state: { prompt: genPrompt, images: genImages },
    dispatch: genDispatch,
  } = useGenContext();
  const user = useAppSelector((state) => state.user.user);
  const isSubscribed = Boolean(user && user.subscriptionType !== "free");
  const isAuthenticated = useAppSelector((state) => state.user.isAuthenticated);
  const authLoading = useAppSelector((state) => state.user.authLoading);
  const quotas = useAppSelector((state) => state.user.quotas);
  const hasActiveJobs = Boolean(user?.activeJobs && user.activeJobs.length > 0);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const dailyGensSummaryRef = useRef<HTMLDivElement>(null);
  const scrollParentRef = useRef<HTMLElement | Window | null>(null);
  const [showCompactSummary, setShowCompactSummary] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [images, setImages] = useState<File[]>(initialConfig?.images || []);
  const [selectedModes, setSelectedModes] = useState<{
    isPublic: boolean;
    is18Plus: boolean;
    isTextImage: boolean;
  }>(
    initialConfig?.selectedModes || {
      isPublic: true,
      is18Plus: false,
      isTextImage: false,
    }
  );
  const [aspectRatio, setAspectRatio] = useState(
    initialConfig?.aspectRatio || "1:1"
  );
  const [selectedModel, setSelectedModel] = useState("auto");
  const [totalImages, setTotalImages] = useState(
    initialConfig?.totalImages || 4
  );

  const isGenerating = isSubmitting || hasActiveJobs;
  const quota = quotas.find((q) => q.resourceName === "gen");
  const hasUsedQuota = quota && quota.currentQuota < quota.baseQuota;

  useEffect(() => {
    if (!isSubscribed) {
      dispatch(getQuotas());
    }
  }, [isSubscribed, dispatch]);

  useEffect(() => {
    if (containerRef.current) {
      scrollParentRef.current = findScrollParent(containerRef.current);
    }
  }, []);

  useEffect(() => {
    if (
      isSubscribed ||
      !hasUsedQuota ||
      !dailyGensSummaryRef.current ||
      !scrollParentRef.current ||
      collapsed
    )
      return;

    const handleScroll = () => {
      if (dailyGensSummaryRef.current) {
        const summaryRect = dailyGensSummaryRef.current.getBoundingClientRect();
        setShowCompactSummary(summaryRect.bottom < 50);
      }
    };

    const scrollElement = scrollParentRef.current;

    if (scrollElement === window) {
      window.addEventListener("scroll", handleScroll);
    } else {
      (scrollElement as HTMLElement).addEventListener("scroll", handleScroll);
    }

    handleScroll();

    return () => {
      if (scrollElement === window) {
        window.removeEventListener("scroll", handleScroll);
      } else {
        (scrollElement as HTMLElement).removeEventListener(
          "scroll",
          handleScroll
        );
      }
    };
  }, [isSubscribed, hasUsedQuota, collapsed]);

  useEffect(() => {
    if (genImages && genImages.length > 0 && images.length === 0) {
      const fileImages = genImages.filter(
        (img) => img instanceof File
      ) as File[];
      if (fileImages.length > 0) {
        setImages(fileImages);
      }
    }
  }, [genImages]);

  useEffect(() => {
    const promptFromUrl = searchParams?.get("prompt");
    if (promptFromUrl && !genPrompt) {
      genDispatch({
        type: "SET_PROMPT",
        payload: decodeURIComponent(promptFromUrl),
      });
    }
  }, [searchParams]);

  const handleModelChange = (model: string) => {
    setSelectedModel(model);
  };

  const handlePromptChange = (newPrompt: string) => {
    genDispatch({ type: "SET_PROMPT", payload: newPrompt });
  };

  const handleImagesChange = (newImages: File[]) => {
    setImages(newImages);
    genDispatch({ type: "SET_IMAGES", payload: newImages });
  };

  const handleRemoveImage = (index: number) => {
    const newImages = [...images];
    newImages.splice(index, 1);
    handleImagesChange(newImages);
  };

  const handleCreate = async () => {
    const prompt = genPrompt?.trim();
    if (!prompt || isGenerating) return;

    setIsSubmitting(true);

    try {
      if (!isAuthenticated && !authLoading) {
        const generationDraft = {
          prompt,
          images: images.map((file) => URL.createObjectURL(file)),
          imageMask: null,
          aspectRatio,
          model: selectedModel,
          numImages: totalImages,
          viewMode: selectedModes.isPublic ? "public" : "private",
          timestamp: Date.now(),
        };
        localStorage.setItem(
          "generationDraft",
          JSON.stringify(generationDraft)
        );
        dispatch(setShowSignupModal(true));
        return;
      }

      const viewMode: ViewMode = selectedModes.isPublic
        ? ViewMode.Public
        : ViewMode.Private;

      if (existingRoomUUID) {
        await dispatch(
          getNewGenerations({
            prompt,
            inspiration: images,
            model: selectedModel,
            numImages: totalImages,
            defaultAspectRatio: aspectRatio,
            viewMode,
          })
        ).unwrap();

        genDispatch({ type: "SET_PROMPT", payload: "" });
        genDispatch({ type: "SET_IMAGES", payload: [] });
        setImages([]);
        handleCollapse();
      } else {
        const ideaUUID = await dispatch(
          createRoom({
            prompt,
            inspiration: images,
            model: selectedModel,
            numImages: totalImages,
            defaultAspectRatio: aspectRatio,
            viewMode,
          })
        ).unwrap();

        if (ideaUUID) {
          router.push(`/idea/${ideaUUID}`);
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePromptKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleCreate();
    }
  };

  const handleExpand = () => {
    if (!isGenerating) {
      setExpanded(true);
    }
  };

  const handleCollapse = () => {
    setExpanded(false);
    if (onClose) onClose();
  };

  useDeepCompareEffect(() => {
    if (initialConfig) {
      setImages(initialConfig.images || []);
      setSelectedModes(
        initialConfig.selectedModes || {
          isPublic: true,
          is18Plus: false,
          isTextImage: false,
        }
      );
      setAspectRatio(initialConfig.aspectRatio || "1:1");
      setSelectedModel("auto");
      setTotalImages(initialConfig.totalImages || 4);
    }
  }, [initialConfig]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        expanded
      ) {
        handleCollapse();
      }
    };

    if (expanded) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [expanded]);

  if (collapsed && !expanded) {
    return (
      <>
        {showCompactSummary && !isSubscribed && hasUsedQuota && (
          <div className={classes.stickyCompactSummary}>
            <DailyGensSummaryCompact />
          </div>
        )}
        <CollapsedView
          prompt={genPrompt || ""}
          images={images}
          onPromptChange={handlePromptChange}
          onImagesChange={handleImagesChange}
          onExpand={handleExpand}
          onSubmit={handleCreate}
          isSubscribed={isSubscribed}
          isGenerating={isGenerating}
        />
      </>
    );
  }

  if (collapsed && expanded) {
    return (
      <>
        <CollapsedView
          prompt={genPrompt || ""}
          images={images}
          onPromptChange={handlePromptChange}
          onImagesChange={handleImagesChange}
          onExpand={handleExpand}
          onSubmit={handleCreate}
          isSubscribed={isSubscribed}
          isGenerating={isGenerating}
        />
        <div className={classes.modalOverlay}>
          <div
            className={cx(classes.container, classes.modal)}
            ref={containerRef}
          >
            <div className={classes.closeButtonContainer}>
              <X onClick={handleCollapse} />
            </div>
            <div className={classes.content}>
              {showHeading && <Header />}
              <div className={classes.promptContainer}>
                {images.length > 0 && (
                  <div className={classes.imagesRow}>
                    {images.map((image, index) => (
                      <div
                        key={`image-${index}`}
                        className={classes.imageWrapper}
                      >
                        <img
                          src={URL.createObjectURL(image)}
                          alt={`Upload ${index + 1}`}
                          className={classes.uploadedImage}
                        />
                        <button
                          className={classes.imageRemoveButton}
                          onClick={() => handleRemoveImage(index)}
                          disabled={isGenerating}
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <input
                  type="text"
                  placeholder="What do you want to make?"
                  value={genPrompt || ""}
                  onChange={(e) => handlePromptChange(e.target.value)}
                  onKeyPress={handlePromptKeyPress}
                  className={classes.promptInput}
                  autoFocus
                  disabled={isGenerating}
                />
              </div>
              <ModeSelect
                selectedModel={selectedModel}
                images={images}
                setImages={setImages}
                selectedModes={selectedModes}
                setSelectedModes={setSelectedModes}
                isSubscribed={isSubscribed}
              />
              <AspectRatioSelect
                selectedModel={selectedModel}
                aspectRatio={aspectRatio}
                setAspectRatio={setAspectRatio}
                isSubscribed={isSubscribed}
              />
              <ModelSelect
                selectedModes={selectedModes}
                selectedModel={selectedModel}
                aspectRatio={aspectRatio}
                hasImages={images.length > 0}
                setSelectedModel={handleModelChange}
                isSubscribed={isSubscribed}
              />
              <TotalImagesSelect
                selectedModel={selectedModel}
                totalImages={totalImages}
                setTotalImages={setTotalImages}
              />
            </div>
            <PreviewOverview
              prompt={genPrompt || ""}
              selectedModes={selectedModes}
              selectedModel={selectedModel}
              aspectRatio={aspectRatio}
              images={images}
              setImages={setImages}
              totalImages={totalImages}
              onCreate={handleCreate}
            />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {showCompactSummary && !isSubscribed && hasUsedQuota && (
        <div className={classes.stickyCompactSummary}>
          <DailyGensSummaryCompact />
        </div>
      )}
      <div
        className={cx(classes.container, onClose && classes.popover)}
        ref={containerRef}
      >
        {onClose && (
          <div className={classes.closeButtonContainer}>
            <X onClick={onClose} />
          </div>
        )}
        <div className={classes.content}>
          {showHeading && !isSubscribed && hasUsedQuota && (
            <div ref={dailyGensSummaryRef}>
              <DailyGensSummary />
            </div>
          )}
          {showHeading && isSubscribed && <Header />}
          <div className={classes.promptContainer}>
            {images.length > 0 && (
              <div className={classes.imagesRow}>
                {images.map((image, index) => (
                  <div key={`image-${index}`} className={classes.imageWrapper}>
                    <img
                      src={URL.createObjectURL(image)}
                      alt={`Upload ${index + 1}`}
                      className={classes.uploadedImage}
                    />
                    <button
                      className={classes.imageRemoveButton}
                      onClick={() => handleRemoveImage(index)}
                      disabled={isGenerating}
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <input
              type="text"
              placeholder="What do you want to make?"
              value={genPrompt || ""}
              onChange={(e) => handlePromptChange(e.target.value)}
              onKeyPress={handlePromptKeyPress}
              className={classes.promptInput}
              autoFocus={Boolean(existingRoomUUID)}
              disabled={isGenerating}
            />
          </div>
          <ModeSelect
            selectedModel={selectedModel}
            images={images}
            setImages={setImages}
            selectedModes={selectedModes}
            setSelectedModes={setSelectedModes}
            isSubscribed={isSubscribed}
          />
          <AspectRatioSelect
            selectedModel={selectedModel}
            aspectRatio={aspectRatio}
            setAspectRatio={setAspectRatio}
            isSubscribed={isSubscribed}
          />
          <ModelSelect
            selectedModes={selectedModes}
            selectedModel={selectedModel}
            aspectRatio={aspectRatio}
            hasImages={images.length > 0}
            setSelectedModel={handleModelChange}
            isSubscribed={isSubscribed}
          />
          <TotalImagesSelect
            selectedModel={selectedModel}
            totalImages={totalImages}
            setTotalImages={setTotalImages}
          />
        </div>
        <PreviewOverview
          prompt={genPrompt || ""}
          selectedModes={selectedModes}
          selectedModel={selectedModel}
          aspectRatio={aspectRatio}
          images={images}
          setImages={setImages}
          totalImages={totalImages}
          onCreate={handleCreate}
        />
      </div>
    </>
  );
};

export default MobileLayout;
